import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { MdAddCircle, MdEdit, MdDelete, MdFilterList } from "react-icons/md";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import loading from "../../../assets/images/common/loading.gif";
import AppDataGrid from "../../../components/common/AppDataGrid";
import { Button, Modal, Form, Col, Row, OverlayTrigger } from "react-bootstrap";

function ParentGroup() {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // ── Filter modal state ──────────────────────────────────────────────────────
  const [showFilterModal, setShowFilterModal] = useState(false);
  const handleShowFilterModal = () => setShowFilterModal(true);
  const handleCloseFilterModal = () => setShowFilterModal(false);

  const [filter, setFilter] = useState({
    parent_group_code: "",
    parent_group_name: "",
  });

  const [appliedFilter, setAppliedFilter] = useState({
    parent_group_code: "",
    parent_group_name: "",
  });

  const fetchParentGroups = async () => {
    try {
      const res = await axios.get(`${baseUrl}/parent_group_read/`, {
        params: { school_id: userObj.school_id },
      });
      const data = Array.isArray(res.data?.parent_groups)
        ? res.data.parent_groups
        : Array.isArray(res.data)
        ? res.data
        : [];
      setRows(data);
    } catch (e) {
      console.error(e);
      setRows([]);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchParentGroups().finally(() => setIsLoading(false));
    // eslint-disable-next-line
  }, []);

  const handleEditClick = (parent_group_id) => {
    const selected = rows.find((r) => r.parent_group_id === parent_group_id);
    if (!selected) return toast.error("Record not found");
    navigate("/finance/parent-group/add", { state: { rowData: selected } });
  };

  const handleDeleteClick = async (parent_group_id) => {
    if (!window.confirm("Are you sure you want to set this record as Inactive?")) return;
    try {
      await axios.post(`${baseUrl}/parent_group_soft_delete/`, {
        parent_group_id,
        school_id: userObj.school_id,
        deleted_by: userObj?.user_name || "admin",
      });
      toast.success("Record set to InActive successfully");
      fetchParentGroups();
    } catch (error) {
      console.error("Delete Error:", error);
      toast.error("Failed to delete record");
    }
  };

  // ── Filter handlers ─────────────────────────────────────────────────────────
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setAppliedFilter({ ...filter });
    setShowFilterModal(false);
  };

  const handleReset = (e) => {
    e.preventDefault();
    setFilter({ parent_group_code: "", parent_group_name: "" });
    setAppliedFilter({ parent_group_code: "", parent_group_name: "" });
    setShowFilterModal(false);
  };

  // ── Search + Filter combined ────────────────────────────────────────────────
  const searchableColumns = useMemo(
    () => [(r) => r.parent_group_code, (r) => r.parent_group_name, (r) => r.is_active],
    []
  );

  const filteredRows = useMemo(() => {
    const q = (searchQuery || "").toUpperCase().replace(/[-\s]+/g, "");

    return (rows || []).filter((r) => {
      const matchesSearch = q
        ? searchableColumns.some((sel) => {
            const v = String(sel(r) || "").toUpperCase().replace(/[-\s]+/g, "");
            return v.includes(q);
          })
        : true;

      const matchesCode = appliedFilter.parent_group_code
        ? String(r.parent_group_code || "")
            .toUpperCase()
            .includes(appliedFilter.parent_group_code.toUpperCase())
        : true;

      const matchesName = appliedFilter.parent_group_name
        ? String(r.parent_group_name || "")
            .toUpperCase()
            .includes(appliedFilter.parent_group_name.toUpperCase())
        : true;

      return matchesSearch && matchesCode && matchesName;
    });
  }, [rows, searchQuery, searchableColumns, appliedFilter]);

  const columns = [
    {
      field: "parent_group_name",
      headerName: "Parent Group Name",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span className="cell-wrap">{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "parent_group_code",
      headerName: "Parent Group Code",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span className="cell-wrap">{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span className="cell-wrap">{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) =>
        params.row?.parent_group_id ? (
          <div className="tableActions">
            <Tooltip title="Edit" arrow>
              <span
                className="commonActionIcons"
                onClick={() => handleEditClick(params.row.parent_group_id)}
              >
                <MdEdit />
              </span>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <span
                className="commonActionIcons"
                onClick={() => handleDeleteClick(params.row.parent_group_id)}
              >
                <MdDelete />
              </span>
            </Tooltip>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="pageBody">
      <ToastContainer />

      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Parent Group</h6>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search."
              value={searchQuery}
              className="searchInput"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-filter">Filter</Tooltip>}
            >
              <Button
                className="secondaryBtn"
                variant="secondary"
                onClick={handleShowFilterModal}
              >
                <MdFilterList />
              </Button>
            </OverlayTrigger>

            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-add">Add</Tooltip>}
            >
              <Button
                className="primaryBtn"
                variant="primary"
                onClick={() => navigate("/finance/parent-group/add")}
              >
                <MdAddCircle />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      </div>

      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRows.length ? filteredRows : []}
              columns={columns}
              getRowId={(r) => r.parent_group_id}
              showToolbar={false}
              pagination
              pageSizeOptions={[10, 20, 50]}
              density="compact"
              maxHeight={550}
            />
          )}
        </div>
      </div>

      {/* ── Filter Modal ───────────────────────────────────────────────────── */}
      <Modal
        show={showFilterModal}
        onHide={handleCloseFilterModal}
        className="commonFilterModal"
      >
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Row>
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="parent_group_code">
                  <Form.Label>Parent Group Code</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Parent Group Code"
                    maxLength={50}
                    value={filter.parent_group_code}
                    onChange={(e) =>
                      setFilter({ ...filter, parent_group_code: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </Col>
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="parent_group_name">
                  <Form.Label>Parent Group Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Parent Group Name"
                    maxLength={200}
                    value={filter.parent_group_name}
                    onChange={(e) =>
                      setFilter({ ...filter, parent_group_name: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button
            variant="secondary"
            className="btn-info clearBtn me-2"
            onClick={handleReset}
          >
            Reset
          </Button>
          <div>
            <Button
              variant="secondary"
              className="btn-danger secondaryBtn me-2"
              onClick={handleCloseFilterModal}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn-success primaryBtn"
              onClick={handleFilterSubmit}
            >
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ParentGroup;